import PrintDashboard from "./pages/printing/PrintDashboard";
// src/App.js
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";

import { AuthProvider } from "./context/AuthContext";
import { SchoolProvider } from "./context/SchoolContext";

// Admin
import TicketTypesAdmin from "./pages/admin/TicketTypesAdmin";
import SlaAdmin from "./pages/admin/SlaAdmin";
import StatusList from "./pages/admin/StatusList";
import CategoryList from "./pages/admin/CategoryList";
import DepartmentList from "./pages/admin/DepartmentList";
import UserAdmin from "./pages/admin/UserAdmin";
import RolesAdmin from "./pages/admin/RolesAdmin";
import DevNotes from "./pages/DevNotes";
import KbAdminPage from "./pages/admin/KbAdminPage";
import SchoolsAdmin from "./pages/admin/SchoolsAdmin";

// Helpdesk Automation
import AutomationAdmin from "./pages/helpdesk/automation/AutomationAdmin";

import RulesList from "./pages/helpdesk/automation/rules/RulesList";
import RuleCreate from "./pages/helpdesk/automation/rules/RuleCreate";
import RuleEdit from "./pages/helpdesk/automation/rules/RuleEdit";

import NotificationsList from "./pages/helpdesk/automation/notifications/NotificationsList";
import NotificationCreate from "./pages/helpdesk/automation/notifications/NotificationCreate";
import NotificationEdit from "./pages/helpdesk/automation/notifications/NotificationEdit";

import RecipeList from "./pages/helpdesk/automation/recipes/RecipeList";
import RecipeCreate from "./pages/helpdesk/automation/recipes/RecipeCreate";
import RecipeEdit from "./pages/helpdesk/automation/recipes/RecipeEdit";

// Components
import PrivateRoute from "./components/PrivateRoute";
import PermissionRoute from "./components/PermissionRoute";
import Layout from "./components/Layout";

// Public pages
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import MicrosoftCallback from "./pages/MicrosoftCallback";

// Helpdesk Tickets
import TicketList from "./pages/helpdesk/tickets/TicketList";
import TicketDetail from "./pages/helpdesk/tickets/TicketDetail";

// Helpdesk Rules
import RulesListPage from "./pages/admin/rules/RulesListPage";
import RuleCreatePage from "./pages/admin/rules/RuleCreatePage";
import RuleEditPage from "./pages/admin/rules/RuleEditPage";

// Helpdesk Notifications
import NotificationsListPage from "./pages/admin/notifications/NotificationsListPage";
import NotificationCreatePage from "./pages/admin/notifications/NotificationCreatePage";
import NotificationEditPage from "./pages/admin/notifications/NotificationEditPage";

// Branding
import HelpdeskBrandingPage from "./pages/admin/HelpdeskBrandingPage";

// Dashboard
import HelpdeskDashboard from "./pages/helpdesk/HelpdeskDashboard";

// Knowledge Base V2 (standalone; existing KB untouched)
import KbV2ArticleList from "./pages/knowledgebase/KbArticleList";
import KbV2ArticleView from "./pages/knowledgebase/KbArticleView";
import KbV2ArticleEdit from "./pages/knowledgebase/KbArticleEdit";

// Knowledge Base Articles
import KbArticlePage from "./pages/helpdesk/kb/KbArticlePage";
import KbListPage from "./pages/helpdesk/kb/KbListPage";

// Problems
import ProblemsList from "./pages/helpdesk/problems/ProblemsList";
import ProblemDetail from "./pages/helpdesk/problems/ProblemDetail";
import ProblemCreate from "./pages/helpdesk/problems/ProblemCreate";

// Contracts
import ContractList from "./pages/contracts/ContractList";
import ContractForm from "./pages/contracts/ContractForm";

// User
import UserList from "./pages/users/UserList";

// Calendar
import CalendarPage from "./pages/helpdesk/CalendarPage";

// Leave
import MyLeavePage from "./pages/leave/MyLeavePage";
import AdminLeavePage from "./pages/leave/AdminLeavePage";
import AdminLeaveAllowancesPage from "./pages/leave/AdminLeaveAllowancesPage";

// Access Control
import AccessControlJobs from "./pages/admin/AccessControlJobs";
import AccessSystems from "./pages/admin/AccessSystems";
import AccessAddToken from "./pages/admin/AccessAddToken";
import AccessControlActions from "./pages/admin/AccessControlActions";
import AccessControlUsers from "./pages/admin/AccessControlUsers";
import AccessControlUserDetail from "./pages/admin/AccessControlUserDetail";
import AccessControlDoors from "./pages/admin/AccessControlDoors";

// Infrastructure
import PublicIpAddresses from "./pages/infrastructure/PublicIpAddresses";

// Arbor
import ArborDashboard from "./pages/arbor/ArborDashboard";

export default function App() {
  return (
    <AuthProvider>
      <SchoolProvider>
        <Router>
          <Routes>
            {/* Public routes */}
            <Route path="/login" element={<Login />} />
            <Route path="/login/callback" element={<MicrosoftCallback />} />

            {/* Protected routes inside Layout */}
            <Route element={<Layout />}>
              {/* Default redirect */}
              <Route path="/" element={<Navigate to="/dashboard" />} />

              {/* Dashboard */}
              <Route
                path="/dashboard"
                element={
                  <PrivateRoute>
                    <Dashboard />
                  </PrivateRoute>
                }
              />

              {/* Helpdesk Tickets */}
              <Route
                path="/helpdesk/tickets"
                element={
                  <PrivateRoute>
                    <TicketList />
                  </PrivateRoute>
                }
              />

              <Route
                path="/helpdesk/tickets/new"
                element={
                  <PrivateRoute>
                    <TicketDetail createMode />
                  </PrivateRoute>
                }
              />
              
              <Route
                path="/helpdesk/tickets/:id"
                element={
                  <PrivateRoute>
                    <TicketDetail />
                  </PrivateRoute>
                }
              />

              {/* Contracts */}
              <Route
                path="/contracts"
                element={
                  <PrivateRoute>
                    <ContractList />
                  </PrivateRoute>
                }
              />

              <Route
                path="/contracts/new"
                element={
                  <PrivateRoute>
                    <ContractForm />
                  </PrivateRoute>
                }
              />

              <Route
                path="/contracts/:id"
                element={
                  <PrivateRoute>
                    <ContractForm />
                  </PrivateRoute>
                }
              />

              {/* User directory */}
              <Route
                path="/users"
                element={
                  <PrivateRoute>
                    <UserList />
                  </PrivateRoute>
                }
              />

              {/* Helpdesk Administration */}
              <Route path="/helpdesk/settings/ticket-types" element={<PrivateRoute><PermissionRoute permission="helpdesk.settings"><TicketTypesAdmin /></PermissionRoute></PrivateRoute>} />
              <Route path="/helpdesk/settings/slas" element={<PrivateRoute><PermissionRoute permission="helpdesk.sla.admin"><SlaAdmin /></PermissionRoute></PrivateRoute>} />

              <Route
                path="/admin/departments"
                element={
                  <PrivateRoute>
                    <PermissionRoute permission="helpdesk.settings">
                      <DepartmentList />
                    </PermissionRoute>
                  </PrivateRoute>
                }
              />

              <Route
                path="/admin/statuses"
                element={
                  <PrivateRoute>
                    <PermissionRoute permission="helpdesk.settings">
                      <StatusList />
                    </PermissionRoute>
                  </PrivateRoute>
                }
              />

              <Route
                path="/admin/categories"
                element={
                  <PrivateRoute>
                    <PermissionRoute permission="helpdesk.settings">
                      <CategoryList />
                    </PermissionRoute>
                  </PrivateRoute>
                }
              />

              {/* Helpdesk Automation */}
              <Route
                path="/helpdesk/automation"
                element={
                  <PrivateRoute>
                    <PermissionRoute permission="helpdesk.automations.manage">
                      <AutomationAdmin />
                    </PermissionRoute>
                  </PrivateRoute>
                }
              >
                <Route
                  index
                  element={<Navigate to="rules" replace />}
                />

                <Route
                  path="rules"
                  element={<RulesList />}
                />

                <Route
                  path="rules/new"
                  element={<RuleCreate />}
                />

                <Route
                  path="rules/:id"
                  element={<RuleEdit />}
                />

                <Route
                  path="recipes"
                  element={<RecipeList />}
                />

                <Route
                  path="recipes/new"
                  element={<RecipeCreate />}
                />

                <Route
                  path="recipes/:id"
                  element={<RecipeEdit />}
                />

                <Route
                  path="notifications"
                  element={<NotificationsList />}
                />

                <Route
                  path="notifications/new"
                  element={<NotificationCreate />}
                />

                <Route
                  path="notifications/:id"
                  element={<NotificationEdit />}
                />

                <Route
                  path="notifications/new"
                  element={<NotificationCreatePage />}
                />

                <Route
                  path="notifications/:id"
                  element={<NotificationEditPage />}
                />
              </Route>

              {/* Staff Administration */}
              <Route
                path="/admin/users"
                element={
                  <PrivateRoute>
                    <PermissionRoute permission="staff.manage">
                      <UserAdmin />
                    </PermissionRoute>
                  </PrivateRoute>
                }
              />

              {/* Roles & Permissions */}
              <Route
                path="/admin/roles"
                element={
                  <PrivateRoute>
                    <PermissionRoute permission="rbac.manage">
                      <RolesAdmin />
                    </PermissionRoute>
                  </PrivateRoute>
                }
              />

              {/* Dev Notes - unchanged for now */}
              <Route
                path="/dev-notes"
                element={
                  <PrivateRoute>
                    <PermissionRoute permission="access.dev-notes">
                      <DevNotes />
                    </PermissionRoute>
                  </PrivateRoute>
                }
              />

              {/* Helpdesk Rules */}
              <Route
                path="/helpdesk/settings/rules"
                element={
                  <PrivateRoute>
                    <PermissionRoute permission="helpdesk.automations.manage">
                      <RulesListPage />
                    </PermissionRoute>
                  </PrivateRoute>
                }
              />

              <Route
                path="/helpdesk/settings/rules/new"
                element={
                  <PrivateRoute>
                    <PermissionRoute permission="helpdesk.automations.manage">
                      <RuleCreatePage />
                    </PermissionRoute>
                  </PrivateRoute>
                }
              />

              <Route
                path="/helpdesk/settings/rules/:id"
                element={
                  <PrivateRoute>
                    <PermissionRoute permission="helpdesk.automations.manage">
                      <RuleEditPage />
                    </PermissionRoute>
                  </PrivateRoute>
                }
              />

              {/* Helpdesk Notifications */}
              <Route
                path="/helpdesk/settings/notifications"
                element={
                  <PrivateRoute>
                    <PermissionRoute permission="helpdesk.settings">
                      <NotificationsListPage />
                    </PermissionRoute>
                  </PrivateRoute>
                }
              />

              <Route
                path="/helpdesk/settings/notifications/new"
                element={
                  <PrivateRoute>
                    <PermissionRoute permission="helpdesk.settings">
                      <NotificationCreatePage />
                    </PermissionRoute>
                  </PrivateRoute>
                }
              />

              <Route
                path="/helpdesk/settings/notifications/:id"
                element={
                  <PrivateRoute>
                    <PermissionRoute permission="helpdesk.settings">
                      <NotificationEditPage />
                    </PermissionRoute>
                  </PrivateRoute>
                }
              />

              {/* Helpdesk Branding */}
              <Route
                path="/helpdesk/settings/branding"
                element={
                  <PrivateRoute>
                    <PermissionRoute permission="helpdesk.settings">
                      <HelpdeskBrandingPage />
                    </PermissionRoute>
                  </PrivateRoute>
                }
              />

              {/* Problems */}
              <Route
                path="/helpdesk/problems"
                element={
                  <PrivateRoute>
                    <ProblemsList />
                  </PrivateRoute>
                }
              />

              <Route
                path="/helpdesk/problems/new"
                element={
                  <PrivateRoute>
                    <ProblemCreate />
                  </PrivateRoute>
                }
              />

              <Route
                path="/helpdesk/problems/:id"
                element={
                  <PrivateRoute>
                    <ProblemDetail />
                  </PrivateRoute>
                }
              />

              {/* Knowledge Base */}
              <Route
                path="/helpdesk/kb"
                element={
                  <PrivateRoute>
                    <PermissionRoute permission="manage.kb">
                      <KbAdminPage />
                    </PermissionRoute>
                  </PrivateRoute>
                }
              />

              <Route
                path="/helpdesk/kb/:id"
                element={
                  <PrivateRoute>
                    <KbArticlePage />
                  </PrivateRoute>
                }
              />

              <Route
                path="/helpdesk/kb/browse"
                element={
                  <PrivateRoute>
                    <KbListPage />
                  </PrivateRoute>
                }
              />

              {/* Knowledge Base V2 */}
              <Route path="/knowledgebase" element={<PrivateRoute><KbV2ArticleList /></PrivateRoute>} />
              <Route path="/knowledgebase/articles/new" element={<PrivateRoute><PermissionRoute permission="manage.kb"><KbV2ArticleEdit /></PermissionRoute></PrivateRoute>} />
              <Route path="/knowledgebase/articles/:id/edit" element={<PrivateRoute><PermissionRoute permission="manage.kb"><KbV2ArticleEdit /></PermissionRoute></PrivateRoute>} />
              <Route path="/knowledgebase/articles/:id" element={<PrivateRoute><KbV2ArticleView /></PrivateRoute>} />

              {/* Helpdesk Dashboard */}
              <Route
                path="/helpdesk/dashboard"
                element={
                  <PrivateRoute>
                    <HelpdeskDashboard />
                  </PrivateRoute>
                }
              />

              {/* Schools */}
              <Route
                path="/admin/schools"
                element={
                  <PrivateRoute>
                    <PermissionRoute permission="admin.schools.manage">
                      <SchoolsAdmin />
                    </PermissionRoute>
                  </PrivateRoute>
                }
              />

              {/* Calendar */}
              <Route
                path="/helpdesk/calendar"
                element={
                  <PrivateRoute>
                    <CalendarPage />
                  </PrivateRoute>
                }
              />

              {/* Leave */}
              <Route
                path="/leave/my"
                element={
                  <PrivateRoute>
                    <MyLeavePage />
                  </PrivateRoute>
                }
              />

              <Route
                path="/leave/admin"
                element={
                  <PrivateRoute>
                    <PermissionRoute permission="approve.leave">
                      <AdminLeavePage />
                    </PermissionRoute>
                  </PrivateRoute>
                }
              />

              <Route
                path="/leave/admin/allowances"
                element={
                  <PrivateRoute>
                    <PermissionRoute permission="approve.leave">
                      <AdminLeaveAllowancesPage />
                    </PermissionRoute>
                  </PrivateRoute>
                }
              />

              {/* Access Control */}
              <Route
                path="/admin/access-control/jobs"
                element={
                  <PrivateRoute>
                    <PermissionRoute permission="access_control.manage">
                      <AccessControlJobs />
                    </PermissionRoute>
                  </PrivateRoute>
                }
              />

              <Route
                path="/admin/access-control/systems"
                element={
                  <PrivateRoute>
                    <PermissionRoute permission="access_control.manage">
                      <AccessSystems />
                    </PermissionRoute>
                  </PrivateRoute>
                }
              />

              <Route
                path="/admin/access-control/add-token"
                element={
                  <PrivateRoute>
                    <PermissionRoute permission="access_control.manage">
                      <AccessAddToken />
                    </PermissionRoute>
                  </PrivateRoute>
                }
              />

              <Route
                path="/admin/access-control/actions"
                element={
                  <PrivateRoute>
                    <PermissionRoute permission="access_control.manage">
                      <AccessControlActions />
                    </PermissionRoute>
                  </PrivateRoute>
                }
              />

              <Route
                path="/admin/access-control/users"
                element={
                  <PrivateRoute>
                    <PermissionRoute permission="access_control.manage">
                      <AccessControlUsers />
                    </PermissionRoute>
                  </PrivateRoute>
                }
              />

              <Route
                path="/admin/access-control/users/:id"
                element={
                  <PrivateRoute>
                    <PermissionRoute permission="access_control.manage">
                      <AccessControlUserDetail />
                    </PermissionRoute>
                  </PrivateRoute>
                }
              />

              <Route
                path="/admin/access-control/doors"
                element={
                  <PrivateRoute>
                    <PermissionRoute permission="access_control.manage">
                      <AccessControlDoors />
                    </PermissionRoute>
                  </PrivateRoute>
                }
              />

              <Route path="/printing" element={<PrivateRoute><PermissionRoute permission="printing.dashboard.view"><PrintDashboard /></PermissionRoute></PrivateRoute>} />
              {/* Infrastructure — Public IP Addresses */}
              <Route
                path="/infrastructure/public-ips"
                element={
                  <PrivateRoute>
                    <PublicIpAddresses />
                  </PrivateRoute>
                }
              />

              {/* Arbor */}
              <Route
                path="/arbor/dashboard"
                element={
                  <PrivateRoute>
                    <ArborDashboard />
                  </PrivateRoute>
                }
              />
            </Route>

            {/* Fallback */}
            <Route path="*" element={<p>Page not found</p>} />
          </Routes>
        </Router>
      </SchoolProvider>
    </AuthProvider>
  );
}